import { reactive, toRaw } from 'vue';
import axios from 'axios';
import { ApplicationConfiguration } from '@/types'; // Adjust the import path as necessary
import { DeploymentPrefix } from '@/shared/models/DeploymentPrefix';

interface ApplicationConfigState {
  configData: ApplicationConfiguration | null;
  loaded: boolean;
  loadingPromise: Promise<void> | null;
}

const state: ApplicationConfigState = reactive({
  configData: null,
  loaded: false,
  loadingPromise: null,
});

const loadApplicationConfig = async (): Promise<void> => {
  if (!state.loaded && !state.loadingPromise) {

    let domain = getApplicationDomain();
    const endpoint = `https://api.${domain}/app/configuration`;

    const mode = import.meta.env.MODE; // Vite's way to access the current mode (development/production)


    state.loadingPromise = axios.get<ApplicationConfiguration>(endpoint)
      .then(response => {
        state.configData = response.data;
        state.loaded = true;
      })
      .catch(async error => {
        console.error('Failed to load config from endpoint, falling back to file', error);
        const configPath = `/src/config/config.${mode}.json`;
        try {
          const response = await fetch(configPath);
          const appConfig: ApplicationConfiguration = await response.json();
          console.warn('Loaded appConfig from file. This should only happen on localhost runs');
          console.warn(appConfig);
          state.configData = appConfig;
          state.loaded = true;
        } catch (fileError) {
          console.error('Failed to load config from file', fileError);
        }
      })
      .finally(() => {
        state.loadingPromise = null;
      });
  }

  return state.loadingPromise || Promise.resolve();
};

const getApplicationDomain = (): string => {
  let domain = window.location.hostname;
  if (domain === 'localhost') {
    domain = 'dev.aplos-nca.com';
    //domain = 'demo.aplos-nca.com';
  }
  return domain;
}

const getApplicationDeploymentPrefix = (): DeploymentPrefix => {

  let domain = getApplicationDomain().trim();
  const parts = domain.split('.')
  if (parts.length < 3) {
    throw new Error(`Domain "${domain}" does not contain a deployment prefix.`)
  }

  const deploymentPrefix = parts[0];

  switch (deploymentPrefix) {
    case "localhost":
    case "dev":
      return DeploymentPrefix.Dev;
    case "demo":
    case "uat":
      return DeploymentPrefix.Demo;
    case "prod":
    case "app":
    default:
      return DeploymentPrefix.Prod;
  }

}

const getApplicationConfigData = (): ApplicationConfiguration | null => {
  const raw = toRaw(state.configData);
  return raw;
};

export const default_max_file_size_in_mb = 50

/**
 * Gets the maximum file upload size in bytes.
 * Automatically loads config if not already loaded.
 * Returns the configured size from API (uploads.max_file_size_in_mb) or falls back to default (50 MB).
 * @returns Promise<number> - Max file size in bytes
 */
const getMaxFileSize = async (): Promise<number> => {
  await loadApplicationConfig();
  const appConfig = getApplicationConfigData();

  // Get max file size in MB from API config, validating it's a valid number
  const configValue = appConfig?.uploads?.max_file_size_in_mb;
  const maxFileSizeInMB = (typeof configValue === 'number' && !isNaN(configValue) && configValue > 0)
    ? configValue
    : default_max_file_size_in_mb;

  // Convert MB to bytes
  return maxFileSizeInMB * 1024 * 1024;
};

export default {
  loadApplicationConfig,
  getApplicationConfigData,
  getMaxFileSize,
  getApplicationDeploymentPrefix
};
